import { cast, equipSkill, setDeck, stepAbilities } from './abilities.ts';
import { botTurn, emptyCell, equipTotem, merge, placeRelic, shatter, stepBoards, summon } from './board.ts';
import { stepCombatAfter, stepCombatBefore } from './combat.ts';
import * as config from './config.ts';
import { BOARD_COOP, BOARD_SOLO, GATE, KINDS, MAX_LEVEL, PITBOSS, RAMP, RUNNER, DECK, MASTERY, SEAL, SKILL_IDS, SKILL_PICK, SURGE, TOTEM_KINDS, clampMastery, deckOf, validDeck, validMastery, type Kind, type SkillId, type TotemKind } from './config.ts';
import type { Ctx } from './context.ts';
import { Crowd } from './crowd.ts';
import type { SimEvent } from './events.ts';
import { buildLayout, type Layout } from './layout.ts';
import { random, seedRng } from './rng.ts';
import { buildEnemyRoutes, type LaneRoute } from './route.ts';
import type { Enemy, Input, MatchStats, PlayerState, SimState } from './state.ts';
import { stepHorde, sweepHorde } from './horde.ts';

/**
 * 2: co-op seats share one altar geometry and start alike. 3: the match tally (stats). 4: the gate
 * falls on GATE.breaches — the match is over and the state stands still from then on.
 */
export const STATE_VERSION = 13;
/** A seat may act at most this many times in one tick; extra actions are dropped. */
export const MAX_ACTIONS_PER_TICK = 4;
const ACTION_TYPES = ['summon', 'merge', 'cast', 'equip_totem', 'equip_skill', 'set_deck', 'shatter'];

export interface PlayerSetup {
  /** A computer-driven stand-in: weaker shots, never acts (the local co-op ally until real players join). */
  bot?: boolean;
  totem?: TotemKind;
  /** The one active skill from the start (else SKILL_PICK.default until an `equip_skill`). */
  skill?: SkillId;
  /** The deck: the spheres the seal summons, taken as given (tests); else DECK.default. */
  deck?: Kind[];
  /** Mastery per sphere in KINDS order (else MASTERY.calibrated for every sphere). */
  mastery?: number[];
  /** Starting board as rows of codes like 'r2' ('' = empty); defaults to the mode's layout. */
  board?: string[][];
}
export interface SimSetup { seed: number; mode: 'solo' | 'coop'; players?: PlayerSetup[] }

/** Routes and layouts are pure functions of the mode; build each once per process. */
const routesCache: LaneRoute[] = buildEnemyRoutes();
const layouts = { solo: buildLayout(false), coop: buildLayout(true) };

/**
 * Everything shared outside the hashed state is frozen: a stray write from a view into config, a layout or
 * a route would split clients silently (the checksum only sees SimState). Typed arrays can't be frozen.
 */
function deepFreeze(v: unknown) {
  if (!v || typeof v !== 'object' || ArrayBuffer.isView(v) || Object.isFrozen(v)) return;
  Object.freeze(v);
  for (const k of Object.keys(v)) deepFreeze((v as Record<string, unknown>)[k]);
}
deepFreeze(layouts); deepFreeze(routesCache);
for (const value of Object.values(config)) deepFreeze(value);

function newPlayer(setup: PlayerSetup, defaults: { board: string[][]; totem: TotemKind }, cells: number): PlayerState {
  return {
    cells: Array.from({ length: cells }, emptyCell),
    sp: SEAL.start, summons: 0,
    totem: setup.totem ?? defaults.totem, totemCharge: 0, totemVersion: 0,
    cooldown: SKILL_IDS.map(() => 0), skill: setup.skill && SKILL_IDS.includes(setup.skill) ? setup.skill : SKILL_PICK.default,
    deck: setup.deck?.length ? KINDS.filter((k) => setup.deck!.includes(k)) : deckOf(DECK.default),
    mastery: KINDS.map((_, i) => clampMastery(setup.mastery?.[i] ?? MASTERY.calibrated)),
    storm: 0, hasteT: 0, burn: null, wave: null, meteors: [],
    bot: !!setup.bot, rate: setup.bot ? 0.6 : 1, power: setup.bot ? 0.7 : 1,
  };
}

/**
 * The deterministic game. Same setup + same inputs per tick ⇒ bit-identical state on every
 * engine. `step` advances one fixed tick (1/60 s) and returns that tick's events.
 */
export class Sim {
  readonly layout: Layout;
  readonly routes = routesCache;
  private readonly ctx: Ctx;

  private constructor(st: SimState) {
    this.layout = layouts[st.mode];
    this.ctx = { st, routes: this.routes, layout: this.layout, crowd: new Crowd(256), ev: [], byId: new Map() };
    this.reindex();
  }

  static create(setup: SimSetup): Sim {
    const coop = setup.mode === 'coop';
    const layout = layouts[setup.mode];
    const seats = setup.players ?? (coop ? [{}, {}] : [{}]);
    if (seats.length !== layout.altars.length) throw new Error(`${setup.mode} needs ${layout.altars.length} players`);
    const st: SimState = {
      version: STATE_VERSION, mode: setup.mode, tick: 0, clock: 0,
      rng: seedRng(setup.seed, 0), looks: seedRng(setup.seed, 1),
      nextId: 1, enemies: [], players: [], barrier: null, barrierSerial: 0,
      attacks: [], lobs: [], venom: [], vortices: [], souls: [],
      nextSpawn: RAMP.firstSoldier, nextBoss: RAMP.firstBoss, nextRunner: RUNNER.firstSpawn, nextPitboss: PITBOSS.firstSpawn, bossToggle: 0, nextSurge: SURGE.first[0] + (SURGE.first[1] - SURGE.first[0]) * random(seedRng(setup.seed, 5)),
      stats: { kills: seats.map(() => 0), bossKills: seats.map(() => 0), leaks: 0 },
    };
    const sim = new Sim(st);
    seats.forEach((seat, i) => {
      // every seat starts alike: a fair match whichever seat you got
      const defaults = { board: coop ? BOARD_COOP : BOARD_SOLO, totem: 'wind' as const };
      const altar = layout.altars[i], p = newPlayer(seat, defaults, altar.cells.length);
      const board = seat.board ?? defaults.board;
      altar.cells.forEach((c, k) => {
        const code = board[c.r]?.[c.c];
        if (!code) return;
        const kind = code[0] as Kind, level = +code.slice(1);
        if (!KINDS.includes(kind) || !Number.isInteger(level) || level < 1 || level > MAX_LEVEL) throw new Error(`bad board code '${code}'`);
        placeRelic(st.rng, p.cells[k], kind, level);
      });
      st.players.push(p);
    });
    sim.ctx.ev.length = 0;
    return sim;
  }

  get state(): SimState { return this.ctx.st; }
  /** Test hook: the live tick context, for tests that stage exact situations. Not for game code. */
  get testCtx(): Ctx { return this.ctx; }
  get tick() { return this.ctx.st.tick; }
  /** The gate has fallen: the match is over, nothing moves any more (ticks still count). */
  get over() { return isOver(this.ctx.st.stats); }
  enemy(id: number): Enemy | null { return this.ctx.byId.get(id) ?? null; }

  /**
   * Advance one tick, applying the inputs scheduled for it. Inputs are put in canonical order first
   * (by seat, keeping each seat's own order), so clients that received the same turn in a different
   * order still agree; malformed inputs and actions over the per-seat cap are dropped before anything runs.
   */
  step(inputs: readonly Input[] = []): SimEvent[] {
    const ctx = this.ctx, st = ctx.st;
    ctx.ev = [];
    // the gate fell: the field stands as it was (turns keep coming until the room closes)
    if (isOver(st.stats)) { st.tick++; return ctx.ev; }
    const ordered = canonicalInputs(inputs, st.players.length);
    // last tick's dead and breached leave now: until then views could read their final state
    sweepHorde(ctx);
    for (const input of ordered) this.apply(input);
    st.players.forEach((p, i) => { if (p.bot) botTurn(ctx, i); });
    stepBoards(ctx);
    stepAbilities(ctx);
    stepCombatBefore(ctx);
    stepHorde(ctx);
    stepCombatAfter(ctx);
    st.clock += 1 / 60;
    st.tick++;
    return ctx.ev;
  }

  private apply({ player, action }: Input) {
    const ctx = this.ctx, p = ctx.st.players[player];
    if (!p || p.bot) return;
    switch (action.type) {
      case 'summon': summon(ctx, player); break;
      case 'merge': if (Number.isInteger(action.from) && Number.isInteger(action.to)) merge(ctx, player, action.from, action.to); break;
      case 'cast': if (SKILL_IDS.includes(action.skill)) cast(ctx, player, action.skill, quantize(action.x), quantize(action.z)); break;
      case 'equip_totem': if (TOTEM_KINDS.includes(action.kind)) equipTotem(ctx, player, action.kind); break;
      case 'equip_skill': if (SKILL_IDS.includes(action.skill)) equipSkill(ctx, player, action.skill); break;
      case 'shatter': if (Number.isInteger(action.cell)) shatter(ctx, player, action.cell); break;
      case 'set_deck': if (Array.isArray(action.kinds) && validDeck(action.kinds) && validMastery(action.mastery, action.kinds.length)) setDeck(ctx, player, action.kinds, action.mastery); break;
    }
  }

  /** A deep, plain copy of the state (JSON-safe). */
  snapshot(): SimState { return clone(this.ctx.st); }

  restore(snapshot: SimState) {
    if (snapshot.version !== STATE_VERSION) throw new Error(`state version ${snapshot.version}, expected ${STATE_VERSION}`);
    if (snapshot.mode !== this.ctx.st.mode) throw new Error('mode mismatch');
    this.ctx.st = clone(snapshot);
    this.reindex();
  }

  static fromSnapshot(snapshot: SimState): Sim {
    if (snapshot.version !== STATE_VERSION) throw new Error(`state version ${snapshot.version}, expected ${STATE_VERSION}`);
    return new Sim(clone(snapshot));
  }

  /** 32-bit hash of the whole state; equal states give equal hashes on every engine. */
  checksum(): number { return hashValue(this.ctx.st); }

  private reindex() {
    this.ctx.byId.clear();
    for (const e of this.ctx.st.enemies) this.ctx.byId.set(e.id, e);
  }
}

/** The gate has taken its last breach. */
export const isOver = (stats: MatchStats) => stats.leaks >= GATE.breaches;

/** Valid inputs, grouped by seat in ascending order (each seat's own order kept), at most MAX_ACTIONS_PER_TICK each. */
function canonicalInputs(inputs: readonly Input[], seats: number): Input[] {
  if (!inputs.length) return [];
  const bySeat: Input[][] = Array.from({ length: seats }, () => []);
  for (const input of inputs) {
    const player = (input as Partial<Input> | null)?.player, action = (input as Partial<Input> | null)?.action;
    if (typeof player !== 'number' || !Number.isInteger(player) || player < 0 || player >= seats) continue;
    if (!action || typeof action !== 'object' || !ACTION_TYPES.includes((action as { type?: string }).type as string)) continue;
    if (bySeat[player].length < MAX_ACTIONS_PER_TICK) bySeat[player].push(input);
  }
  return bySeat.flat();
}

/** Inputs carry centimetre precision on the wire; the local path rounds the same way. */
export const quantize = (v: number) => Math.round(v * 100) / 100;

function clone<T>(v: T): T {
  if (Array.isArray(v)) return v.map(clone) as T;
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v)) out[k] = clone((v as Record<string, unknown>)[k]);
    return out as T;
  }
  return v;
}

/* ---------------- hashing ---------------- */

const f64 = new Float64Array(1), words = new Uint32Array(f64.buffer);
/** murmur3-style mixing of 32-bit words. */
function mix(h: number, k: number) {
  k = Math.imul(k, 0xcc9e2d51); k = (k << 15) | (k >>> 17); k = Math.imul(k, 0x1b873593);
  h ^= k; h = (h << 13) | (h >>> 19);
  return (Math.imul(h, 5) + 0xe6546b64) | 0;
}
function hashInto(h: number, v: unknown): number {
  if (typeof v === 'number') {
    if (v !== v) return mix(h, 8); // NaN's bit pattern is CPU-dependent: one fixed tag
    f64[0] = v === 0 ? 0 : v; // +0 and −0 compare equal; hash them equal too
    return mix(mix(mix(h, 1), words[0]), words[1]);
  }
  if (typeof v === 'string') { h = mix(mix(h, 2), v.length); for (let i = 0; i < v.length; i++) h = mix(h, v.charCodeAt(i)); return h; }
  if (typeof v === 'boolean') return mix(h, v ? 3 : 4);
  if (v === null || v === undefined) return mix(h, 5);
  if (Array.isArray(v)) { h = mix(mix(h, 6), v.length); for (const x of v) h = hashInto(h, x); return h; }
  // fixed key order and undefined = absent (as in JSON): a state rebuilt by a decoder or restored
  // from a snapshot hashes the same as the live one
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o).filter((k) => o[k] !== undefined).sort(); // purity-ok: plain string keys, code-unit order
  h = mix(mix(h, 7), keys.length);
  for (const k of keys) { h = hashInto(h, k); h = hashInto(h, o[k]); }
  return h;
}
export function hashValue(v: unknown): number {
  let h = hashInto(0x9747b28c, v);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}

